import cn from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { useEditorCanvasMouse } from 'components/Editor/EditorContext/actions'
import { getElement } from 'components/Editor/EditorContext/selectors/block'
import EditorElementComponent from 'components/Editor/EditorElements/EditorElementComponent'
import EditorCommentBubble from 'components/comments/EditorCommentBubble'
import EditorCreateComment from 'components/comments/EditorCreateComment'
import { stopPropagation } from 'constants/commonConstans'
import { scrollProps } from 'services/Scroll/ScrollService'
import { setHighlight, updateBlock, updateNodeState } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getBlockWaiting,
  getIsActiveBlock,
  getIsActiveNode,
  getIsCommentedElement,
  getIsCommenting,
  getIsDragging,
  getIsTransforming,
  getNodeState,
  getShowCanvasPlaceholder,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'

import type { INodeV2Props } from '../../cource/layout/Node/types'
import { DragPlaceholder } from '../DisplayProvider'
import { displayProps } from '../DisplayProvider/utils'
import NodeV2Controls from '../NodeV2Controls'
import TransformPreview from '../NodeV2Controls/TransformPreview'
import { useNodeAnimation } from '../formControls/Animation/useNodeAnimation'
import { isAbsolute, isRoot } from '../guards'
import NodeV2Styled from '../styled/NodeV2Styled'
import * as s from './EditorNodeV2.module.scss'

const EditorNodeV2: React.FC<INodeV2Props> = (props) => {
  const { children, node, block, mode, parent, isItem, isSlot, isPreview } = props
  const { editorMode, deviceMode } = mode
  const { uuid: blockId } = block
  const { id: nodeId, elementId } = node
  const element = node.elementId ? getElement(block, node.elementId) : null
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useProjectDispatch()
  const isActiveBlock = useProjectContext(getIsActiveBlock, blockId)
  const isFill = editorMode === AppMode.fill
  const isActive = useProjectContext(getIsActiveNode, node.id)
  const isDragging = useProjectContext(getIsDragging)
  const isTransforming = useProjectContext(getIsTransforming, [node.id, ...node.children])
  const { fill, playing, transform } = useProjectContext(getNodeState, node.id) || {}
  const showPlaceholder = useProjectContext(getShowCanvasPlaceholder, node.id)
  const customMode = useMemo(
    () => ({ ...mode, editorMode: fill ? AppMode.fill : mode.editorMode }),
    [mode, fill],
  )
  const waiting = useProjectContext(getBlockWaiting, block.uuid)
  const isCommented = useProjectContext(getIsCommentedElement, elementId)
  const isCommenting = useProjectContext(getIsCommenting, blockId, elementId)

  const style = useMemo(
    () => mergeNodeStyle(node.style, deviceMode, element?.type),
    [element?.type, deviceMode, node.style],
  )
  const parentStyle = useMemo(
    () => mergeNodeStyle(parent?.style || {}, deviceMode),
    [deviceMode, parent?.style],
  )

  const onChange = useCallback(
    (value: unknown) => {
      dispatch(updateBlock({ id: block.uuid, name: `elements.${element?.id}.value`, value }))
    },
    [dispatch, block.uuid, element?.id],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // const isDelete = isFill && (e.key === 'Backspace' || e.key === 'Delete')
    // const isCut = isFill && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x'
    // const isCopy = isFill && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c'
    // const isPaste = isFill && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v'
    if (fill || (isFill && element)) {
      stopPropagation(e)
    }
  }

  const highlight = useMemo(
    () => ({
      onMouseOut: (e: React.MouseEvent) => {
        e.stopPropagation()
        dispatch(setHighlight(null))
      },
      onMouseOver: (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!isDragging) {
          dispatch(setHighlight(node.id))
        }
      },
    }),
    [dispatch, isDragging, node.id],
  )

  const handlers = useEditorCanvasMouse({ mode: customMode.editorMode, blockId, nodeId })
  const animation = useNodeAnimation(node)

  useEffect(() => {
    if (!isActive) {
      dispatch(
        updateNodeState({
          id: node.id,
          state: { isEditingGrid: false, transform: null, fill: false },
        }),
      )
    }
  }, [dispatch, node.id, isActive])

  return (
    <>
      <NodeV2Styled
        {...scrollProps(node.id)}
        {...displayProps(node, style, { isItem, isRoot: isRoot(block, node) })}
        {...(!isSlot && handlers)}
        {...(!isSlot && highlight)}
        {...animation}
        $element={element}
        $isRoot={isRoot(block, node)}
        $isSlot={isSlot}
        $node={node}
        $parent={parent}
        $parentStyle={parentStyle}
        $style={style}
        className={cn({
          [s.root]: isActiveBlock,
          [s.stub]: !isPreview && isActive && isDragging,
          [s.hidden]: !playing && Boolean(transform),
          [s.transparent]: isDragging && isAbsolute(style) /* || isAbsolute(parentStyle) */,
        })}
        key={String(playing)}
        onKeyDown={handleKeyDown}
        ref={ref}
      >
        {showPlaceholder && (
          <DragPlaceholder block={block} mode={customMode} node={node} nodeRef={ref} />
        )}
        {element ? (
          <>
            <EditorElementComponent
              block={block}
              element={element}
              mode={customMode}
              onChange={onChange}
              waiting={waiting}
              isV2
            />
            {isCommenting && <EditorCreateComment blockId={block.uuid} elementId={elementId} />}
            {isCommented && (
              <EditorCommentBubble blockId={blockId} elementId={element.id} nodeId={nodeId} />
            )}
          </>
        ) : (
          children
        )}
        {!isTransforming && !isPreview && !playing && (
          <NodeV2Controls
            block={block}
            element={element}
            mode={mode}
            node={node}
            nodeRef={ref}
            parent={parent}
          />
        )}
      </NodeV2Styled>

      {Boolean(transform) && !playing && (
        <TransformPreview {...props} nodeRef={ref} transform={transform} />
      )}
    </>
  )
}

EditorNodeV2.displayName = 'EditorNodeV2'

export default React.memo(EditorNodeV2)
